import React, { useState, useEffect, useCallback, useMemo } from "react";
import _ from "lodash";
import Autosuggest from "react-autosuggest";
import {
    ISearchSuggestion,
    ISearchSuggestionSection,
    ISearchSuggestionResponse,
} from "../../../Models";
import { toast } from "react-toastify";

interface ISearchSuggestionProps {
    value: string;
    inputProps: {};
    formSubmitFlagged: boolean;
    fetchSuggestions: any;
    onChange: (value: string) => void;
    onSelect: (value: string, filter: string) => void;
}

export default function SearchSuggestion({
    value,
    inputProps,
    formSubmitFlagged,
    fetchSuggestions,
    onChange,
    onSelect,
}: ISearchSuggestionProps) {
    const [suggestionsValue, setSuggestionsValue] = useState<string>("");
    const [suggestions, setSuggestions] = useState<ISearchSuggestionSection[]>(
        []
    );
    const [suggestionsCache, setSuggestionsCache] = useState<
        ISearchSuggestionSection[]
    >([]);
    const [suggestionsRequestsInProgress, setSuggestionsRequestsInProgress] =
        useState<number>(0);
    const [skipSuggestionsUpdate, setSkipSuggestionsUpdate] =
        useState<boolean>(false);

    const debouncedFetch = useMemo(
        () =>
            _.debounce((value: string) => {
                setSuggestionsRequestsInProgress((prev) => prev + 1);

                fetchSuggestions(value)
                    .then((response: ISearchSuggestionResponse[]) => {
                        const result =
                            response &&
                            response
                                .map((section) => ({
                                    type: section.type,
                                    suggestions: [
                                        ...new Set(section.suggestions),
                                    ].map((s) => ({ value: s })),
                                }))
                                .filter(
                                    (section) => section.suggestions.length > 0
                                );

                        if (!skipSuggestionsUpdate) {
                            setSuggestions(result);
                        }

                        setSuggestionsValue(value);
                        setSuggestionsCache(result);
                    })
                    .catch(() => {
                        toast.error("Something went wrong");
                    })
                    .finally(() => {
                        setSuggestionsRequestsInProgress((prev) => {
                            const newCount = prev - 1;
                            if (newCount === 0) {
                                setSkipSuggestionsUpdate(false);
                            }
                            return newCount;
                        });
                        Autosuggest;
                    });
            }, 500),
        []
    );

    useEffect(() => {
        if (formSubmitFlagged && suggestionsRequestsInProgress > 0) {
            setSkipSuggestionsUpdate(true);
        }
    }, [formSubmitFlagged, suggestionsRequestsInProgress]);

    useEffect(() => {
        return () => {
            debouncedFetch.cancel();
        };
    }, [debouncedFetch]);

    const handleFetchRequested = ({ value: inputVal }) => {
        if (inputVal !== suggestionsValue) {
            debouncedFetch(inputVal);
        } else {
            setSuggestions(suggestionsCache);
        }
    };

    const handleClearRequested = () => {
        setSuggestions([]);
    };

    const handleSuggestionSelected = (
        event,
        { suggestion, suggestionValue, sectionIndex }
    ) => {
        onSelect(suggestionValue, suggestions[sectionIndex].type);
    };

    const handleInputChange = (event, { newValue }) => {
        onChange(newValue);
    };

    const getSuggestionValue = (suggestion: ISearchSuggestion): string => {
        return suggestion.value || "";
    };

    const renderSuggestion = (suggestion: ISearchSuggestion) => {
        return <span>{suggestion.value}</span>;
    };

    const renderSectionTitle = (section: ISearchSuggestionSection) => {
        return <strong>{section.type}</strong>;
    };

    const getSectionSuggestions = (
        section: ISearchSuggestionSection
    ): ISearchSuggestion[] => {
        return section.suggestions;
    };

    return (
        <Autosuggest
            multiSection={true}
            suggestions={suggestions}
            onSuggestionsFetchRequested={handleFetchRequested}
            onSuggestionsClearRequested={handleClearRequested}
            onSuggestionSelected={handleSuggestionSelected}
            focusInputOnSuggestionClick={false}
            getSuggestionValue={getSuggestionValue}
            getSectionSuggestions={getSectionSuggestions}
            renderSuggestion={renderSuggestion}
            renderSectionTitle={renderSectionTitle}
            inputProps={{
                ...inputProps,
                value,
                onChange: handleInputChange,
            }}
        />
    );
}
